import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Search, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

// `phone` is the recipient when the caller has a conversation but no lead object
// (the WhatsApp inbox); `lead` covers the lead-detail chat. Jalpi needs one of
// them — unlike the Meta path it does not look the number up from the lead.
export default function WhatsAppTemplatePicker({ open, onOpenChange, leadId, onSent, lead, phone, sender }) {
  const [templates, setTemplates] = useState([]);
  const [crmTemplates, setCrmTemplates] = useState([]);
  // "crm" = our own copy, editable in Patient Messaging and sent as free-form
  // text; "approved" = the WABA's Meta-approved templates. Default to ours: they
  // are the clinic's real wording, and the approved list on a shared number can
  // be mostly templates belonging to whatever else uses it.
  const [source, setSource] = useState("crm");
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [params, setParams] = useState({});
  const [sending, setSending] = useState(false);

  // Provider-aware: match the currently-selected sender. Default to jalpi.
  const provider = sender?.provider || "jalpi";
  const accountId = sender?.account_id;

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSelected(null);
    setParams({});
    setSearch("");
    setSource("crm");
    const approved = provider === "meta"
      ? api.get("/whatsapp/templates")
      : api.get("/jalpi/templates", { params: accountId ? { account_id: accountId } : {} });
    // Both lists up front: the two tabs must be switchable without a spinner,
    // and an empty approved list is itself worth seeing straight away.
    Promise.allSettled([approved, api.get("/comms/sendable-templates")])
      .then(([a, c]) => {
        if (a.status === "fulfilled") setTemplates(a.value.data || []);
        else toast.error("Failed to load approved templates");
        if (c.status === "fulfilled") setCrmTemplates(c.value.data || []);
      })
      .finally(() => setLoading(false));
  }, [open, provider, accountId]);

  const q = search.toLowerCase();
  const filtered = templates.filter((t) =>
    t.name.toLowerCase().includes(q) || (t.category || "").toLowerCase().includes(q)
  );
  const filteredCrm = crmTemplates.filter((t) =>
    t.name.toLowerCase().includes(q) || t.body.toLowerCase().includes(q)
  );

  const extractParams = (template) => {
    const paramList = [];
    for (const comp of template.components || []) {
      const matches = (comp.text || "").match(/\{\{(\d+)\}\}/g);
      if (matches) {
        for (const m of matches) {
          const idx = m.replace(/[{}]/g, "");
          paramList.push({ component_type: comp.type, index: idx });
        }
      }
    }
    return paramList;
  };

  const selectTemplate = (tpl) => {
    setSelected(tpl);
    const p = {};
    extractParams(tpl).forEach((param) => {
      p[`${param.component_type}_${param.index}`] = "";
    });
    setParams(p);
  };

  const sendTemplate = async () => {
    if (!selected) return;
    setSending(true);

    const values = extractParams(selected).map((p) => params[`${p.component_type}_${p.index}`] || "");

    try {
      if (selected._crm) {
        // Our own copy. Placeholders are filled server-side from the lead and
        // the branch, so there is nothing to collect here.
        await api.post("/comms/send-template", {
          event: selected.event,
          lead_id: leadId || null,
          phone: phone || lead?.whatsapp || lead?.mobile || null,
        });
      } else if (provider === "meta") {
        // Meta's nested components shape.
        await api.post("/whatsapp/send-template", {
          lead_id: leadId,
          template_name: selected.name,
          language_code: selected.language || "en",
          components: [
            { type: "body", parameters: values.map((text) => ({ type: "text", text })) },
          ],
        });
      } else {
        // Jalpi flat body_parameters filling {{1}}..{{n}}.
        await api.post("/jalpi/send-template", {
          lead_id: leadId,
          phone: phone || lead?.whatsapp || lead?.mobile,
          account_id: accountId,
          template_name: selected.name,
          language_code: selected.language || "en",
          body_parameters: values.map((text) => ({ type: "text", text })),
        });
      }
      toast.success("Template message sent");
      onSent?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Send WhatsApp Template</DialogTitle>
        </DialogHeader>

        {!selected ? (
          <div className="flex-1 overflow-y-auto space-y-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search templates..."
                className="pl-9"
              />
            </div>

            <div className="flex rounded-lg bg-navy/5 p-0.5 text-xs font-medium">
              {[["crm", `Our templates (${crmTemplates.length})`],
                ["approved", `Approved (${templates.length})`]].map(([key, label]) => (
                <button key={key} onClick={() => setSource(key)}
                        className={`flex-1 rounded-md px-3 py-1.5 transition-colors ${
                          source === key ? "bg-white text-navy shadow-sm" : "text-muted-foreground hover:text-navy"}`}>
                  {label}
                </button>
              ))}
            </div>

            {loading && <div className="text-center text-sm text-muted-foreground py-8">Loading templates...</div>}

            {!loading && source === "crm" && (
              filteredCrm.length === 0 ? (
                <div className="text-center text-sm text-muted-foreground py-8">
                  {crmTemplates.length === 0
                    ? "No WhatsApp templates yet. Add them in Settings → Patient Messaging → Templates."
                    : "No templates match your search."}
                </div>
              ) : filteredCrm.map((tpl) => (
                <button
                  key={tpl.event}
                  onClick={() => setSelected({ ...tpl, _crm: true })}
                  className="w-full text-left rounded-lg border border-navy/10 p-3 hover:bg-cream/50 transition-colors"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-sm">{tpl.name}</span>
                    {tpl.requires_window && (
                      <Badge variant="outline" className="shrink-0 text-[10px] text-amber-700 border-amber-300">
                        24h window only
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{tpl.body}</p>
                  {tpl.buttons?.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {tpl.buttons.map((b) => (
                        <span key={b.id} className="rounded border border-navy/15 px-1.5 py-0.5 text-[10px] text-navy">
                          {b.label}
                        </span>
                      ))}
                    </div>
                  )}
                </button>
              ))
            )}

            {!loading && source === "approved" && filtered.length === 0 && (
              <div className="text-center text-sm text-muted-foreground py-8">
                {templates.length === 0
                  ? (provider === "meta"
                    ? "No approved templates found. Create templates in Meta Business Manager."
                    : "No approved templates on this number yet. An admin can create one in Integrations → WhatsApp (Jalpi) → Templates.")
                  : "No templates match your search."}
              </div>
            )}

            {source === "approved" && filtered.map((tpl) => (
              <button
                key={`${tpl.name}-${tpl.language}`}
                onClick={() => selectTemplate(tpl)}
                className="w-full text-left rounded-lg border border-navy/10 p-3 hover:bg-cream/50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">{tpl.name}</span>
                  <Badge variant="outline" className="text-[10px]">{tpl.category}</Badge>
                </div>
                {tpl.components?.map((c, i) =>
                  c.type === "BODY" ? (
                    <p key={i} className="text-xs text-muted-foreground mt-1 line-clamp-2">{c.text}</p>
                  ) : null
                )}
              </button>
            ))}
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-4">
            {selected._crm ? <CrmTemplatePreview template={selected} /> : <TemplatePreview
              template={selected}
              values={Object.fromEntries(
                extractParams(selected).map((p) => [p.index, params[`${p.component_type}_${p.index}`]]))}
            />}

            {!selected._crm && extractParams(selected).length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Template Parameters</p>
                {extractParams(selected).map((p) => {
                  const key = `${p.component_type}_${p.index}`;
                  return (
                    <div key={key}>
                      <label className="text-xs text-muted-foreground">{p.component_type} — Parameter {`{{${p.index}}}`}</label>
                      <Input
                        value={params[key] || ""}
                        onChange={(e) => setParams((prev) => ({ ...prev, [key]: e.target.value }))}
                        placeholder={`Value for {{${p.index}}}`}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            <Button variant="ghost" onClick={() => setSelected(null)} className="text-sm">
              &larr; Back to templates
            </Button>
          </div>
        )}

        <DialogFooter>
          {selected && (
            <Button onClick={sendTemplate} disabled={sending} className="bg-green-600 text-white hover:bg-green-700">
              <Send className="mr-2 h-4 w-4" /> {sending ? "Sending..." : "Send Template"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Our own template, as the patient will read it. {placeholders} stay highlighted
// rather than being guessed at: they are filled on the server from the lead and
// the branch, so showing a made-up value here would be a lie about what is sent.
function CrmTemplatePreview({ template }) {
  return (
    <div className="rounded-lg border border-navy/10 bg-cream/40 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="truncate text-sm font-medium">{template.name}</span>
        <Badge variant="outline" className="text-[10px]">Our template</Badge>
      </div>
      <div className="max-w-[92%] rounded-lg rounded-tl-none bg-white px-3 py-2 text-sm shadow-sm">
        <p className="whitespace-pre-wrap break-words">
          {String(template.body || "").split(/(\{\w+\})/g).map((part, i) =>
            /^\{\w+\}$/.test(part)
              ? <span key={i} className="rounded bg-amber-100 px-1 text-amber-800">{part}</span>
              : part)}
        </p>
      </div>
      {template.buttons?.length > 0 && (
        <div className="mt-1 flex max-w-[92%] flex-col gap-1">
          {template.buttons.map((b) => (
            <div key={b.id} className="rounded-lg bg-white px-3 py-1.5 text-center text-sm font-medium text-sky-700 shadow-sm">
              {b.label}
            </div>
          ))}
        </div>
      )}
      <p className="mt-2 text-[11px] text-muted-foreground">
        {template.requires_window
          ? "Sends as a normal message. Only works if this contact messaged us in the last 24 hours."
          : "Sends as a normal message, or falls back to the approved template if the 24-hour window has closed."}
        {template.buttons?.length > 0 && " Buttons appear only inside the window."}
      </p>
    </div>
  );
}

// What the patient will actually see: header in bold, the body with its own line
// breaks and *bold* markup, footer muted, buttons as chips. A {{n}} placeholder is
// highlighted until a value is typed below, then shows that value — so staff read
// the real message instead of raw "HEADER: / BODY:" parts.
function TemplatePreview({ template, values = {} }) {
  const comps = template.components || [];
  const find = (type) => comps.find((c) => String(c.type || "").toUpperCase() === type);
  const header = find("HEADER");
  const body = find("BODY");
  const footer = find("FOOTER");
  const buttons = find("BUTTONS")?.buttons || [];

  const renderText = (text) =>
    String(text || "").split(/(\{\{\d+\}\}|\*[^*\n]+\*)/g).map((part, i) => {
      const v = part.match(/^\{\{(\d+)\}\}$/);
      if (v) {
        const val = values[v[1]];
        return val
          ? <span key={i} className="font-medium text-ink">{val}</span>
          : <span key={i} className="rounded bg-amber-100 px-1 text-amber-800">{part}</span>;
      }
      if (/^\*[^*\n]+\*$/.test(part)) return <strong key={i}>{part.slice(1, -1)}</strong>;
      return part;
    });

  const isTextHeader = header && (!header.format || String(header.format).toUpperCase() === "TEXT");

  return (
    <div className="rounded-lg border border-navy/10 bg-cream/40 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="truncate text-sm font-medium">{template.name}</span>
        {template.category && <Badge variant="outline" className="text-[10px]">{template.category}</Badge>}
      </div>
      <div className="max-w-[92%] rounded-lg rounded-tl-none bg-white px-3 py-2 text-sm shadow-sm">
        {header && (isTextHeader
          ? <p className="mb-1 font-semibold">{renderText(header.text)}</p>
          : <p className="mb-1 rounded bg-navy/5 px-2 py-3 text-center text-xs text-muted-foreground">
              {String(header.format).toLowerCase()} header
            </p>)}
        {body && <p className="whitespace-pre-wrap break-words">{renderText(body.text)}</p>}
        {footer && <p className="mt-1 text-xs text-muted-foreground">{footer.text}</p>}
      </div>
      {buttons.length > 0 && (
        <div className="mt-1 flex max-w-[92%] flex-col gap-1">
          {buttons.map((b, i) => (
            <div key={i} className="rounded-lg bg-white px-3 py-1.5 text-center text-sm font-medium text-sky-700 shadow-sm">
              {b.text}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
